/**
 * Parte stampata (VIZ, "visual inspection zone") dei documenti italiani: i
 * dati che la MRZ non ha e che servono alla questura.
 *
 * - Fronte della CIE: comune che l'ha emessa ("COMUNE DI ..."), luogo di
 *   nascita "COMUNE (PR)", date di nascita, emissione e scadenza.
 * - Retro della CIE: codice fiscale stampato (se il codice a barre non si
 *   legge) e indirizzo di residenza.
 * - Passaporto italiano: questura che l'ha rilasciato, luogo di nascita,
 *   date.
 *
 * Il testo stampato non ha cifre di controllo e l'OCR storpia spesso qualche
 * lettera: qui si estraggono i candidati, il backend li confronta con le
 * tabelle (comuni per somiglianza, date in ordine) e tutto resta "da
 * controllare".
 */

import { trovaCodiceFiscale } from "./codicefiscale";

/** Parole intere di sole lettere (eventuali apostrofi), maiuscole. */
function parole(testo) {
  return String(testo || "")
    .toUpperCase()
    .replace(/[^A-ZÀ-Ý'\s-]/g, " ")
    .split(/\s+/)
    .filter(Boolean);
}

/**
 * Nome di luogo pulito: senza i pezzetti spuri che l'OCR aggiunge ai lati
 * (bordi, timbri, fondini), cioe' le parole di una o due lettere in testa e
 * in coda.
 */
function nomeLuogo(testo) {
  const p = parole(testo);
  while (p.length && p[0].length <= 2) {
    p.shift();
  }
  while (p.length && p[p.length - 1].length <= 2) {
    p.pop();
  }
  return p.join(" ");
}

/**
 * Indirizzo senza la coda sporca: dopo il numero civico l'OCR legge il
 * fondino come lettere ("VIA SALARIA, 712 ue PS s"). Dopo il civico si
 * tengono una lettera maiuscola sola ("12 A"), le parole maiuscole vere e i
 * numeri ("INT. 3"), e ci si ferma alla prima parola minuscola o di due lettere.
 */
function senzaCodaSporca(indirizzo) {
  const parti = indirizzo.split(" ");
  const civico = parti.findIndex((p) => /\d/.test(p));
  if (civico < 0) {
    return indirizzo;
  }
  let fine = civico + 1;
  while (fine < parti.length && (/^[A-Z]$/.test(parti[fine]) || /^[A-ZÀ-Ý'.]{3,}$/.test(parti[fine]) || /^\d+[A-Z]?[,./]?$/.test(parti[fine]))) {
    fine++;
  }
  return parti.slice(0, fine).join(" ").replace(/[,.\s]+$/, "");
}

/** Tutte le date gg.mm.aaaa (o con / e -) del testo, in AAAA-MM-GG e in ordine. */
function date(testo) {
  const trovate = [];
  const re = /(\d{2})\s?[./,-]\s?(\d{2})\s?[./,-]\s?((?:19|20)\d{2})/g;
  let m;
  while ((m = re.exec(testo))) {
    const g = parseInt(m[1], 10);
    const me = parseInt(m[2], 10);
    if (g >= 1 && g <= 31 && me >= 1 && me <= 12) {
      trovate.push(m[3] + "-" + m[2] + "-" + m[1]);
    }
  }
  return [...new Set(trovate)].sort();
}

/** La riga che segue l'etichetta (saltando la traduzione inglese in corsivo). */
function dopoEtichetta(righe, etichetta, inglese) {
  const i = righe.findIndex((r) => etichetta.test(r));
  if (i < 0) {
    return "";
  }
  // Valore sulla stessa riga dell'etichetta, dopo di essa.
  const stessa = righe[i].replace(etichetta, "").replace(inglese || /$^/, "");
  if (nomeLuogo(stessa).length >= 3) {
    return stessa;
  }
  for (const r of righe.slice(i + 1, i + 3)) {
    if (inglese && inglese.test(r)) {
      continue;
    }
    return r;
  }
  return "";
}

// Parole delle etichette della CIE: non sono mai il valore di un campo.
const ETICHETTE = /^(COGNOME|SURNAME|NOME|NAME|LUOGO|DATA|NASCITA|PLACE|DATE|BIRTH|SESSO|SEX|STATURA|HEIGHT|CITTADINANZA|NATIONALITY|EMISSIONE|ISSUING|SCADENZA|EXPIRY|FIRMA|COMUNE|MUNICIPALITY|CARTA|IDENTITA|IDENTITÀ|IDENTITY|CARD|REPUBBLICA|ITALIANA|MINISTERO|INTERNO)$/;

/** Distanza di modifica tra due parole (lettere da cambiare, togliere o aggiungere). */
function distanza(a, b) {
  const riga = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    let diagonale = riga[0];
    riga[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const sopra = riga[j];
      riga[j] = Math.min(riga[j] + 1, riga[j - 1] + 1, diagonale + (a[i - 1] === b[j - 1] ? 0 : 1));
      diagonale = sopra;
    }
  }
  return riga[b.length];
}

/** Parola che e' un'etichetta della CIE, anche storpiata ("NONE" per "NOME"). */
function eEtichetta(parola) {
  if (ETICHETTE.test(parola)) {
    return true;
  }
  const tolleranza = parola.length >= 7 ? 2 : parola.length >= 4 ? 1 : 0;
  return tolleranza > 0 && ETICHETTE.source.replace(/[\^$()]/g, "").split("|").some((e) => Math.abs(e.length - parola.length) <= tolleranza && distanza(parola, e) <= tolleranza);
}

/**
 * Riga fatta solo di etichette: se il nome del comune non si legge, sotto la
 * sua etichetta c'e' gia' quella dopo ("COGNOME / SURNAME"). Una parola sola
 * conta solo se e' esattamente un'etichetta: "NOLE" e' un comune, non "NOME"
 * storpiato.
 */
function soloEtichette(riga) {
  const p = parole(riga);
  return p.length === 1 ? ETICHETTE.test(p[0]) : p.length > 1 && p.every(eEtichetta);
}

/**
 * Cognome o nome dalla riga sotto l'etichetta. La riga deve essere pulita:
 * quasi solo lettere maiuscole (il valore e' stampato cosi'), almeno tre
 * lettere, nessuna parola che sia un'etichetta. Le lettere sparse che l'OCR
 * legge dal fondino si scartano: meglio un campo vuoto che uno sbagliato.
 * Le particelle di due lettere restano ("DE ROSSI", "LO BUE").
 */
function nomePersona(riga) {
  // Via i segni del fondino ai lati ("ROSSI. — ===" -> "ROSSI"), anche le
  // lettere sole ("GUIDELLI Ì"): le particelle hanno almeno due lettere.
  const testo = String(riga || "")
    .trim()
    .replace(/^[^A-Za-zÀ-ÿ]+|[^A-Za-zÀ-ÿ]+$/g, "")
    .replace(/^([A-Za-zÀ-ÿ][^A-Za-zÀ-ÿ]+)+|([^A-Za-zÀ-ÿ]+[A-Za-zÀ-ÿ])+$/g, "");
  const piene = testo.replace(/\s/g, "");
  const maiuscole = (testo.match(/[A-ZÀ-Ý]/g) || []).length;
  const lettere = (testo.match(/[A-Za-zÀ-ÿ'-]/g) || []).length;
  if (maiuscole < 3 || lettere < piene.length * 0.85 || maiuscole < lettere * 0.9) {
    return "";
  }
  const p = parole(testo);
  if (!p.length || p.length > 4 || p.some(eEtichetta) || p.every((x) => x.length <= 2)) {
    return "";
  }
  return p.join(" ");
}

/**
 * Valore di un campo della CIE: la riga subito sotto l'etichetta, o quella
 * dopo se in mezzo c'e' la traduzione inglese o una riga senza lettere.
 */
function valoreSotto(righe, etichetta, inglese, valido) {
  const i = righe.findIndex((r) => etichetta.test(r));
  if (i < 0) {
    return "";
  }
  for (const r of righe.slice(i + 1, i + 3)) {
    const v = valido(r);
    if (v) {
      return v;
    }
    if (!inglese.test(r) && /[A-Za-z]/.test(r)) {
      return "";
    }
  }
  return "";
}

/**
 * Numero della CIE: due lettere (la prima C), cinque cifre, due lettere, in
 * alto a destra del fronte ("CA00000AA"). Nelle cifre l'OCR scambia O e 0,
 * I e 1: si correggono solo li', dove ci vanno per forza cifre.
 */
function numeroCie(testo) {
  const m = String(testo || "")
    .toUpperCase()
    .match(/\b(C[A-Z])\s?([0-9OQDIL]{5})\s?([A-Z]{2})\b/);
  if (!m) {
    return "";
  }
  return m[1] + m[2].replace(/[OQD]/g, "0").replace(/[IL]/g, "1") + m[3];
}

/**
 * Campi del fronte della CIE che stanno anche nella MRZ del retro: servono
 * quando si fotografa solo il fronte, o quando la MRZ non si legge.
 */
function campiFronteCie(righe) {
  const testo = righe.join("\n");
  const ris = {
    cognome: valoreSotto(righe, /COGNOME/i, /SURNAME/i, nomePersona),
    nome: valoreSotto(righe, /(^|[^G])\bNOME\b/i, /\bNAME\b/i, nomePersona),
    numero: numeroCie(testo),
    sesso: "",
    cittadinanza: "",
    dataNascita: "",
    dataRilascio: "",
    dataScadenza: ""
  };
  // Sesso, statura e cittadinanza sulla stessa riga: "F 180 ITA". Con
  // l'impaginazione "testo sparso" sono righe separate, una dopo l'altra.
  for (const t of [testo, righe.join(" ")]) {
    const m = t.toUpperCase().match(/(?:^|\s)([MF])\s+\d{3}\s+([A-Z]{3})(?=\s|$)/m);
    if (m) {
      ris.sesso = m[1];
      ris.cittadinanza = m[2];
      break;
    }
  }
  // Se la statura manca o e' letta male, sesso e cittadinanza sotto le loro
  // etichette: una lettera sola, tre lettere sole.
  if (!ris.sesso) {
    // Sulla riga di statura e cittadinanza, tra i segni del fondino.
    const riga = righe.find((r) => /\b\d{3}\s+[A-Z]{3}\b/.test(r));
    const prima = riga ? riga.slice(0, riga.search(/\b\d{3}\s+[A-Z]{3}\b/)) : "";
    const lettere = prima.split(/[^A-Za-z]+/).filter(Boolean);
    if (lettere.length && /^[MF]$/.test(lettere[lettere.length - 1])) {
      ris.sesso = lettere[lettere.length - 1];
    }
  }
  if (!ris.sesso) {
    // Impaginazione sparsa: una riga con la sola M o F tra l'etichetta del
    // sesso e quella dell'emissione.
    const da = righe.findIndex((r) => /SESSO|^\s*SEX\b/i.test(r));
    const a = righe.findIndex((r, i) => i > da && /EMISSIONE|ISSUING/i.test(r));
    if (da >= 0) {
      const sole = righe.slice(da + 1, a > da ? a : da + 10).filter((r) => /^\s*[MF]\s*$/.test(r));
      if (sole.length === 1) {
        ris.sesso = sole[0].trim();
      }
    }
  }
  if (!ris.cittadinanza) {
    const m = righe.join(" ").toUpperCase().match(/(?:^|\s)\d{3}\s+([A-Z]{3})(?=\s|$)/);
    ris.cittadinanza = m ? m[1] : "";
  }
  // Data di nascita: in fondo alla riga del luogo ("... (VA) 30.12.1964").
  const nascita = righe.find((r) => /\(\s*[A-Z]{2}\s*\)/.test(r.toUpperCase()) && date(r).length === 1);
  if (nascita) {
    ris.dataNascita = date(nascita)[0];
  }
  // Emissione e scadenza: sotto le loro etichette, spesso sulla stessa riga.
  const i = righe.findIndex((r) => /EMISSIONE|ISSUING/i.test(r));
  if (i >= 0) {
    const d = date(righe.slice(i, i + 4).join(" "));
    // La CIE vale al massimo dieci anni (fino al compleanno dopo): una
    // scadenza piu' lontana e' una cifra letta male ("2083").
    const anni = d.length === 2 ? parseInt(d[1], 10) - parseInt(d[0], 10) : -1;
    if (anni >= 0 && anni <= 11) {
      [ris.dataRilascio, ris.dataScadenza] = d;
    }
  }
  return ris;
}

/**
 * Legge la parte stampata dalle righe dell'OCR.
 *
 * @param {string[]} righe testo riconosciuto, una riga per elemento
 * @returns {object|null} dati trovati, null se non c'e' nulla di utile
 */
export function leggiViz(righe) {
  const testo = righe.join("\n");
  // Fronte della CIE: il titolo, o le etichette bilingui quando il titolo
  // non si legge (fondino, riflesso).
  const cie =
    (/CARTA\s+D.?\s*IDENTIT/i.test(testo) && /REPUBBLICA|MINISTERO|COMUNE\s+DI/i.test(testo)) ||
    (/COGNOME|SURNAME/i.test(testo) && /MUNICIPALITY|COMUNE\s+DI/i.test(testo));
  const passaporto = /PASSAPORTO|PASSPORT/i.test(testo) && /REPUBBLICA\s+ITALIANA/i.test(testo);

  // Comune che ha emesso la CIE. L'etichetta e' minuta e l'OCR ne perde
  // spesso l'inizio ("UNE DI / MUNICIPALITY"): basta la parte inglese, e
  // tutto cio' che le sta prima sulla riga e' etichetta.
  const comune = nomeLuogo(dopoEtichetta(righe, /^.*(?:MUNICIPALIT|M[UV]NICIP|NICIPALIT|CIPALITY)\S*|COMUNE\s+DI\s*\/?/i, /MUNICIPALITY/i)) || "";

  // Questura che ha rilasciato il passaporto ("QUESTURA DI ROMA").
  const questura = (testo.match(/QUESTURA\s+DI\s+([A-ZÀ-Ý' ]{3,40})/i) || [])[1];

  // Luoghi con la sigla della provincia: "PINO SULLA SPONDA ... (VA)".
  const luoghi = [];
  const reLuogo = /([A-ZÀ-Ý][A-ZÀ-Ý' -]{1,60}?)\s*\(\s*([A-Z]{2})\s*\)/g;
  let m;
  while ((m = reLuogo.exec(testo.toUpperCase()))) {
    const nome = nomeLuogo(m[1]);
    if (nome.length >= 2) {
      luoghi.push({ luogo: nome, provincia: m[2] });
    }
  }

  const indirizzo = senzaCodaSporca(
    // Anche qui l'inizio dell'etichetta si perde spesso ("VIZIO D' RESIDENZA
    // / RESIDENCE"): conta "RESIDENZA", e cio' che le sta attorno e' etichetta.
    dopoEtichetta(righe, /^.*\bRESIDEN[ZC]\S*(?:\s*\/\s*(?:RES\S*)?)?/i, /RESIDENCE/i)
      .replace(/[^A-Za-zÀ-ÿ0-9',./\s-]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
  );

  const cf = trovaCodiceFiscale(testo.replace(/\s+/g, " "));
  const ris = {
    cie,
    passaporto,
    comuneRilascio: cie && comune.length >= 3 && !soloEtichette(comune) ? comune : questura ? nomeLuogo(questura) : "",
    luoghi,
    date: date(testo),
    indirizzo: indirizzo.length >= 5 ? indirizzo : "",
    codiceFiscale: cf,
    fronte: cie ? campiFronteCie(righe) : null
  };
  const fronte = ris.fronte && Object.values(ris.fronte).some(Boolean);
  if (!fronte) {
    ris.fronte = null;
  }
  const utile = ris.comuneRilascio || ris.luoghi.length || ris.date.length || ris.indirizzo || ris.codiceFiscale || fronte;
  return utile ? ris : null;
}

/** Campi del fronte di piu' letture insieme. */
function unisciFronti(fronti) {
  const tutti = fronti.filter(Boolean);
  if (!tutti.length) {
    return null;
  }
  // Per ogni campo il valore letto piu' volte (le prove dell'OCR sbagliano
  // in modi diversi, quella giusta si ripete); a parita' il primo.
  const ris = {};
  for (const k of ["cognome", "nome", "numero", "sesso", "cittadinanza", "dataNascita", "dataRilascio", "dataScadenza"]) {
    const voti = new Map();
    tutti.filter((f) => f[k]).forEach((f) => voti.set(f[k], (voti.get(f[k]) || 0) + 1));
    ris[k] = [...voti.entries()].reduce((m, x) => (x[1] > m[1] ? x : m), ["", 0])[0];
  }
  return ris;
}

/** Unisce le letture di piu' foto (fronte e retro): la prima che ha il dato vince. */
export function unisciViz(letture) {
  const tutte = letture.filter(Boolean);
  if (!tutte.length) {
    return null;
  }
  const primo = (k) => (tutte.find((v) => v[k]) || {})[k] || "";
  return {
    cie: tutte.some((v) => v.cie),
    passaporto: tutte.some((v) => v.passaporto),
    comuneRilascio: primo("comuneRilascio"),
    luoghi: tutte.flatMap((v) => v.luoghi),
    date: [...new Set(tutte.flatMap((v) => v.date))].sort(),
    indirizzo: primo("indirizzo"),
    codiceFiscale: tutte.map((v) => v.codiceFiscale).find(Boolean) || null,
    fronte: unisciFronti(tutte.map((v) => v.fronte))
  };
}

export default { leggiViz, unisciViz };
